import { useEffect } from "react";
import { X } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { nok, kompakt, monthLabel, CAT_LABEL } from "./budsjettUtils";

/* Beautiful per-month transaction breakdown for the cash flow. */
export default function MonthDrawer({ entry, tx, scen, onClose }) {
  const { t } = useLang();

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const inns = (tx || []).filter((e) => e.kind === "in");
  const uts = (tx || []).filter((e) => e.kind === "out");
  const net = (entry?.inn || 0) - (entry?.ut || 0);

  const sourceLabel = (e) => {
    if (e.source === "tickets") return t("Ticket sales");
    if (e.source === "sponsor") return t("Sponsor");
    if (e.source === "revenue") return t("Other revenue");
    if (e.source === "contingency") return t("Contingency");
    return t(CAT_LABEL[e.source] || e.source);
  };

  const Rows = ({ items, prefix }) => (
    <div>
      {items.map((e, i) => (
        <div
          key={i}
          className="flex items-center gap-3 py-2 border-t border-[#F1EEE7] first:border-0"
          data-testid={`${prefix}-${i}`}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-medium text-neutral-800 truncate">{e.label}</p>
            <p className="text-[10px] uppercase tracking-[0.1em] text-neutral-400">{sourceLabel(e)}</p>
          </div>
          <span className={`text-[13px] font-semibold tabular-nums ${e.kind === "in" ? "text-emerald-700" : "text-neutral-700"}`}>
            {e.kind === "in" ? "+" : "−"}{nok(e.amount)}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cash-month-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="sak-drawer relative w-full max-w-[460px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">{t("Transactions")} · {scen}</p>
            <h2 className="text-[19px] font-semibold mt-0.5">{monthLabel(entry?.month)}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="cash-month-drawer-close">
            <X size={17} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {(inns.length === 0 && uts.length === 0) ? (
            <p className="text-[12.5px] text-neutral-400 text-center py-12" data-testid="cash-month-empty">{t("No payments this month.")}</p>
          ) : (
            <>
              {inns.length > 0 && (
                <div className="mb-6" data-testid="cash-month-in">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700">{t("Money in")}</p>
                    <p className="text-[12px] font-semibold tabular-nums text-emerald-700">+{nok(entry?.inn || 0)}</p>
                  </div>
                  <Rows items={inns} prefix="cash-tx-in" />
                </div>
              )}
              {uts.length > 0 && (
                <div className="mb-6" data-testid="cash-month-out">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{t("Money out")}</p>
                    <p className="text-[12px] font-semibold tabular-nums text-neutral-600">−{nok(entry?.ut || 0)}</p>
                  </div>
                  <Rows items={uts} prefix="cash-tx-out" />
                </div>
              )}
            </>
          )}

          <div className="border-t border-neutral-900 pt-3 mt-2">
            <div className="flex items-center justify-between py-1">
              <span className="text-[12px] text-neutral-500">{t("Net this month")}</span>
              <span className={`text-[13px] font-semibold tabular-nums ${net < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="cash-month-net">
                {net >= 0 ? "+" : ""}{nok(net)}
              </span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="text-[12px] text-neutral-500">{t("Closing balance")}</span>
              <span className={`text-[17px] font-semibold tabular-nums ${entry?.closing < 0 ? "text-red-600" : ""}`} data-testid="cash-month-closing">
                {kompakt(entry?.closing || 0)}
              </span>
            </div>
          </div>
          <p className="text-[10.5px] text-neutral-400 mt-3">{t("All amounts incl. VAT")}.</p>
        </div>
      </aside>
    </div>
  );
}
